import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../lib/api";

/**
 * SRC-styled newsletter block — full-width black/white split with a
 * borderless input that underlines on focus. Copy + submit endpoint
 * come from the template config so each store type gets its own
 * "First Access" headline.
 */
export default function Newsletter({ config, slug }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const copy = config?.copy?.newsletter || {};
  const eyebrow = copy.eyebrow || "— First Access";
  const headline1 = copy.headline1 || "Be the first";
  const headline2 = copy.headline2 || "on the next drop.";
  const subhead = copy.subhead ||
    "Subscribers get a 24-hour early access window and 10% off their first order.";
  const finePrint = copy.finePrint ||
    "By subscribing you agree to our Privacy Policy. Unsubscribe any time.";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      // Public site → hit the slug-scoped endpoint; showroom → generic.
      const url = slug ? `/public/site/${slug}/newsletter` : "/newsletter";
      await api.post(url, { email });
      toast.success(copy.successMessage || "You're in. Watch for the next drop.");
      setEmail("");
    } catch (err) {
      toast.error(copy.errorMessage || "Couldn't subscribe. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="newsletter" className="bg-black text-white">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-20 sm:py-28 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <p className="text-[11px] tracking-[0.3em] uppercase text-neutral-400" data-testid="newsletter-eyebrow">{eyebrow}</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[0.95]">
            <span data-testid="newsletter-headline-1">{headline1}</span> <br /> <span data-testid="newsletter-headline-2">{headline2}</span>
          </h2>
          <p className="mt-5 text-neutral-300 max-w-md text-sm leading-relaxed" data-testid="newsletter-subhead">{subhead}</p>
        </div>
        <form onSubmit={onSubmit} className="flex flex-col gap-3">
          <div className="flex border-b border-white/40 focus-within:border-white transition-colors">
            <input
              data-testid="newsletter-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={copy.placeholder || "your@email.com"}
              className="flex-1 bg-transparent py-4 outline-none placeholder:text-neutral-500 text-base"
            />
            <button
              data-testid="newsletter-submit"
              type="submit"
              disabled={loading}
              className="px-4 py-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] disabled:opacity-50"
            >
              {loading ? "..." : (copy.submitLabel || "Subscribe")}
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
          <p className="text-[11px] text-neutral-500 tracking-wide" data-testid="newsletter-fineprint">{finePrint}</p>
        </form>
      </div>
    </section>
  );
}
